import React, { useState } from "react";
import "./index.css";
import { FaEdit, FaTrash, FaEye, FaComment } from "react-icons/fa";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"; // use as MovertIcon
import SensorOccupiedIcon from "@mui/icons-material/Sensors"; // sensor icon

const DynamicTable = ({
  columns,
  data,
  onEdit,
  onView,
  onDelete,
  onComment,
  view = true,
  edit = true,
  deleteAction = true,
  isLoading = false,
  multipleaction = true,
  fiter,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState([]);
  const [movertToggles, setMovertToggles] = useState({});

  const rowsPerPage = 8;

  const toggleExpand = (id) =>
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleMovert = (id) =>
    setMovertToggles((prev) => ({ ...prev, [id]: !prev[id] }));

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    if (typeof aValue === "string") {
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
  });

  const filteredData = sortedData.filter((row) =>
    columns.some(
      (col) =>
        col.key !== "action" &&
        row[col.key] != null &&
        row[col.key].toString().toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const start = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(start, start + rowsPerPage);

  const handlePageChange = (page) =>
    page >= 1 && page <= totalPages && setCurrentPage(page);

  const handleSort = (key) => {
    if (key === "action") return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const renderSkeleton = () =>
    Array.from({ length: rowsPerPage }).map((_, idx) => (
      <tr key={idx}>
        {columns.map((_, cidx) => (
          <td key={cidx}>
            <div className="skeleton-line" />
          </td>
        ))}
      </tr>
    ));

  return (
    <div className="table-container">
      <div className="filter-container">
        {fiter && (
          <input
            className="filter-input"
            placeholder="Search..."
            value={filterText}
            disabled={isLoading}
            onChange={(e) => {
              setFilterText(e.target.value);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      <div className="table-responsive">
        <table className="reusable-table">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className={col.key !== "action" ? "sortable" : ""}
                  onClick={() => handleSort(col.key)}
                >
                  <div className="header-content">
                    {col.header}
                    {!isLoading && sortConfig.key === col.key && (
                      <span className="sort-icon">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderSkeleton()
            ) : currentData.length > 0 ? (
              currentData.map((row, rIdx) => (
                <React.Fragment key={row.id}>
                  <tr className="parent-row">
                    {columns.map((col, cIdx) => (
                      <td key={cIdx}>
                        {col.key === "action" ? (
                          <div className="action-buttons">
                            {edit && (
                              <FaEdit
                                className="action-icon edit-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(row.id, row);
                                }}
                                title="Edit"
                              />
                            )}
                            {view && (
                              <FaEye
                                className="action-icon view-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView(row.id, row);
                                }}
                                title="View"
                              />
                            )}
                            {deleteAction && (
                              <FaTrash
                                className="action-icon delete-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(row.id, row);
                                }}
                                title="Delete"
                              />
                            )}

                            {/* Movert (DragIndicatorIcon) */}
                            {multipleaction && (
                              <DragIndicatorIcon
                                className="action-icon movert-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMovert(row.id);
                                }}
                                title="Movert"
                              />
                            )}

                            {/* Show Comment & Sensor when toggled */}
                            {movertToggles[row.id] && (
                              <>
                                <FaComment
                                  className="action-icon comment-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onComment(row.id, row);
                                  }}
                                  title="Comment"
                                />
                                <SensorOccupiedIcon
                                  className="action-icon sensor-icon"
                                  title="Sensor Occupied"
                                />
                              </>
                            )}

                            {Array.isArray(row.subtasks) &&
                              row.subtasks.length > 0 && (
                                <span
                                  className="dropdown-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(row.id);
                                  }}
                                  title={
                                    expandedRows.includes(row.id)
                                      ? "Collapse"
                                      : "Expand"
                                  }
                                >
                                  {expandedRows.includes(row.id) ? "▾" : "▸"}
                                </span>
                              )}
                          </div>
                        ) : (
                          row[col.key]
                        )}
                      </td>
                    ))}
                  </tr>

                  {expandedRows.includes(row.id) &&
                    row.subtasks.map((sub, sIdx) => (
                      <tr key={sub.id} className="subtask-row">
                        {columns.map((col, cIdx) => (
                          <td key={cIdx}>
                            {col.key === "action" ? (
                              <div className="action-buttons">
                                {edit && (
                                  <FaEdit
                                    className="action-icon edit-icon"
                                    onClick={() => onEdit(sub.id, sub)}
                                    title="Edit Subtask"
                                  />
                                )}
                                {view && (
                                  <FaEye
                                    className="action-icon view-icon"
                                    onClick={() => onView(sub.id, sub)}
                                    title="View Subtask"
                                  />
                                )}
                                {deleteAction && (
                                  <FaTrash
                                    className="action-icon delete-icon"
                                    onClick={() => onDelete(sub.id, sub)}
                                    title="Delete Subtask"
                                  />
                                )}
                                {multipleaction && (
                                  <DragIndicatorIcon
                                    className="action-icon movert-icon"
                                    onClick={() => toggleMovert(sub.id)}
                                    title="Movert Subtask"
                                  />
                                )}
                                {movertToggles[sub.id] && (
                                  <>
                                    <FaComment
                                      className="action-icon comment-icon"
                                      onClick={() => onComment(sub.id, sub)}
                                      title="Comment"
                                    />
                                    <SensorOccupiedIcon
                                      className="action-icon sensor-icon"
                                      title="Sensor Occupied"
                                    />
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="subtask-cell">
                                {sub[col.key] ?? "-"}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-data">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && totalRows > 0 && (
        <div className="pagination-container">
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .slice(
              Math.max(0, currentPage - 3),
              Math.min(totalPages, currentPage + 2)
            )
            .map((p) => (
              <button
                key={p}
                className={`pagination-button ${
                  p === currentPage ? "active" : ""
                }`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            ))}
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
        </div>
      )}
    </div>
  );
};

export default DynamicTable;
